(() => {
  const loader = document.getElementById('eraspace-loader');
  if (!loader) return;

  const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index.html';
  const params = new URLSearchParams(window.location.search);
  const loaderMode = params.get('eraspace_loader');
  const hashMode = window.location.hash || '';
  const qaHold = params.has('loaderqa') || loaderMode === 'qa' || hashMode.includes('eraspace-loader-qa');
  const reset = loaderMode === 'reset' || hashMode.includes('eraspace-loader-reset');
  const forceReplay = loaderMode === '1' || (hashMode.includes('eraspace-loader') && !qaHold && !hashMode.includes('eraspace-loader-reset'));
  const force = qaHold || reset || forceReplay;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const storageKey = 'eraspaceLoaderLastShown';
  const cooldown = 24 * 60 * 60 * 1000;
  const now = Date.now();

  if (reset) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (_) {}
  }

  const lastShown = Number(window.localStorage?.getItem(storageKey) || 0);
  const shouldShow = isHomepage && !reduceMotion && (force || !lastShown || now - lastShown > cooldown);

  if (!shouldShow) {
    loader.remove();
    return;
  }

  if (!force) {
    try {
      window.localStorage.setItem(storageKey, String(now));
    } catch (_) {}
  }

  const phase = loader.querySelector('[data-eraspace-loader-phase]');
  const progressText = loader.querySelector('[data-eraspace-loader-progress]');
  const stage = loader.querySelector('.eraspace-loader__stage');
  const rocketScene = loader.querySelector('.eraspace-loader__rocket-scene');
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const easeOutQuart = (value) => 1 - Math.pow(1 - clamp(value, 0, 1), 4);
  const phases = [
    { at: 0.06, text: 'Preparing storefront' },
    { at: 0.3, text: 'Loading latest deals' },
    { at: 0.56, text: 'Tuning delivery route' },
    { at: 0.8, text: 'Opening Eraspace' },
  ];

  const minimumVisibleTime = reduceMotion ? 600 : qaHold ? 60000 : 2400;
  const maximumVisibleTime = reduceMotion ? 900 : qaHold ? 75000 : 3400;
  const startedAt = performance.now();

  let pageLoaded = document.readyState === 'complete';
  let currentProgress = 0;
  let phaseIndex = -1;
  let lastFrameTime = startedAt;
  let progressFrame;
  let maxTimer;
  let phaseTimer;
  let removeTimer;
  let clearStateTimer;
  let hidden = false;
  let completing = false;

  const setRocketTravel = (ratio) => {
    if (!rocketScene || !stage) return;

    const stageWidth = stage.clientWidth || window.innerWidth;
    const rocketWidth = rocketScene.offsetWidth || Math.min(stageWidth * 0.42, 500);
    const isMobile = window.innerWidth < 700;
    const eased = 0.5 - Math.cos(clamp(ratio, 0, 1) * Math.PI) / 2;
    const start = isMobile ? -rocketWidth * 0.46 : -rocketWidth * 0.38;
    const end = isMobile ? stageWidth - rocketWidth * 1.05 : stageWidth - rocketWidth * 1.08;
    const x = start + (end - start) * eased;
    const arc = -Math.sin(eased * Math.PI) * (isMobile ? 24 : 36);
    const rotate = -10 + eased * 15;
    const scale = isMobile ? 0.98 + eased * 0.02 : 0.96 + eased * 0.04;
    const opacity = clamp(eased * 4.5, 0, 1);

    rocketScene.style.setProperty('--eraspace-loader-rocket-x', `${x.toFixed(1)}px`);
    rocketScene.style.setProperty('--eraspace-loader-rocket-arc-y', `${arc.toFixed(1)}px`);
    rocketScene.style.setProperty('--eraspace-loader-rocket-rotate', `${rotate.toFixed(2)}deg`);
    rocketScene.style.setProperty('--eraspace-loader-rocket-scale', scale.toFixed(3));
    rocketScene.style.setProperty('--eraspace-loader-rocket-opacity', opacity.toFixed(3));
  };

  const setProgress = (value) => {
    const next = clamp(value, 0, 100);
    currentProgress = next;
    loader.style.setProperty('--eraspace-loader-progress-scale', (next / 100).toFixed(4));
    setRocketTravel(next / 100);

    if (progressText) {
      progressText.textContent = next >= 100 ? 'Eraspace is ready' : `Loading Eraspace ${Math.round(next)} percent`;
    }
  };

  const setPhase = (text, immediate = false) => {
    if (!phase || phase.textContent === text) return;

    window.clearTimeout(phaseTimer);

    if (immediate) {
      phase.classList.remove('is-switching');
      phase.textContent = text;
      return;
    }

    phase.classList.add('is-switching');
    phaseTimer = window.setTimeout(() => {
      phase.textContent = text;
      requestAnimationFrame(() => phase.classList.remove('is-switching'));
    }, 160);
  };

  const updatePhaseForProgress = () => {
    if (completing || hidden) return;

    const ratio = currentProgress / 100;
    const nextIndex = phases.reduce((match, item, index) => (ratio >= item.at ? index : match), 0);

    if (nextIndex !== phaseIndex) {
      phaseIndex = nextIndex;
      setPhase(phases[nextIndex].text);
    }
  };

  const setPageRevealState = (state, enabled = true) => {
    const className = `eraspace-loader-${state}`;
    document.documentElement.classList.toggle(className, enabled);
    if (document.body) document.body.classList.toggle(className, enabled);
  };

  const clearPageRevealState = () => {
    ['preparing', 'revealing'].forEach((state) => setPageRevealState(state, false));
  };

  const unlock = () => {
    document.documentElement.classList.remove('eraspace-loader-lock');
    if (document.body) document.body.classList.remove('eraspace-loader-lock');
  };

  const removeLoader = () => {
    loader.hidden = true;
    loader.remove();
  };

  const getTargetProgress = (elapsed) => {
    if (completing) return 100;

    const timeRatio = clamp(elapsed / maximumVisibleTime, 0, 1);
    const baseTarget = 10 + easeOutQuart(timeRatio) * 76;

    if (!pageLoaded) return Math.min(84, baseTarget);

    const readyRatio = clamp(elapsed / minimumVisibleTime, 0, 1);
    return Math.min(97, Math.max(baseTarget, 50 + easeOutQuart(readyRatio) * 44));
  };

  const updateProgress = (time) => {
    if (hidden) return;

    const elapsed = time - startedAt;
    const delta = clamp(time - lastFrameTime, 0, 100) / (1000 / 60);
    lastFrameTime = time;

    const target = getTargetProgress(elapsed);
    const distance = target - currentProgress;
    const softStep = distance * 0.075 * Math.max(delta, 0.5);
    const maxStep = completing ? 5.2 : 1.25;
    const minStep = completing ? 0.16 : 0.018;
    const step = clamp(softStep, distance > 0 ? minStep : -maxStep, maxStep * Math.max(delta, 0.5));

    setProgress(distance > 0 ? Math.min(target, currentProgress + step) : Math.max(target, currentProgress + step));
    updatePhaseForProgress();
    progressFrame = requestAnimationFrame(updateProgress);
  };

  const hide = () => {
    if (hidden) return;

    hidden = true;
    window.clearTimeout(maxTimer);
    window.clearTimeout(phaseTimer);
    window.clearTimeout(removeTimer);
    window.clearTimeout(clearStateTimer);
    window.cancelAnimationFrame(progressFrame);

    setProgress(100);
    setPageRevealState('revealing', true);
    loader.classList.add('is-hiding');
    loader.setAttribute('aria-busy', 'false');
    unlock();

    removeTimer = window.setTimeout(removeLoader, 780);
    clearStateTimer = window.setTimeout(clearPageRevealState, 1450);
  };

  const completeThenHide = () => {
    if (hidden || completing) return;

    completing = true;
    pageLoaded = true;
    loader.classList.add('is-complete');
    setPhase('Ready to explore.', true);

    const settle = () => {
      if (hidden) return;

      if (currentProgress < 99.8) {
        setProgress(currentProgress + (100 - currentProgress) * 0.34);
        progressFrame = requestAnimationFrame(settle);
        return;
      }

      setProgress(100);
      window.setTimeout(hide, 360);
    };

    window.cancelAnimationFrame(progressFrame);
    progressFrame = requestAnimationFrame(settle);
  };

  const requestHideAfterMinimum = () => {
    pageLoaded = true;
    const elapsed = performance.now() - startedAt;
    window.setTimeout(completeThenHide, Math.max(460, minimumVisibleTime - elapsed));
  };

  document.documentElement.classList.add('eraspace-loader-lock');
  if (document.body) document.body.classList.add('eraspace-loader-lock');
  setPageRevealState('preparing', true);

  loader.hidden = false;
  setProgress(0);
  setPhase('Preparing storefront', true);

  progressFrame = requestAnimationFrame(updateProgress);

  requestAnimationFrame(() => {
    loader.classList.add('is-visible');
  });

  if (pageLoaded) {
    window.setTimeout(requestHideAfterMinimum, 0);
  } else {
    window.addEventListener('load', requestHideAfterMinimum, { once: true });
  }

  maxTimer = window.setTimeout(() => {
    pageLoaded = true;
    completeThenHide();
  }, maximumVisibleTime);
})();
